import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { supabase } from '../../lib/supabase';
import type { PortalClass, Recording } from '../../types/portal';
import { ChevronLeft, BookOpen, ArrowDownUp } from 'lucide-react';
import LessonRow from '../../components/student/LessonRow';
import { isNewRecording } from '../../lib/portalHelpers';

const StudentCourseLessonsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useStudentAuth();

  const [cls, setCls] = useState<PortalClass | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const togglingRef = useRef<Set<string>>(new Set());
  const [ascending, setAscending] = useState(true);
  const [visibleCount, setVisibleCount] = useState(10);

  const openLesson = (rec: Recording) => {
    setWatchedIds(prev => new Set(prev).add(rec.id));
    navigate(`/dashboard/kurse/${id}/lektion/${rec.id}`);
  };

  const load = useCallback(async () => {
    if (!id) return;
    const [{ data: classData }, { data: recData }, { data: progressData }, { data: positionsData }] = await Promise.all([
      supabase.from('portal_classes').select('*').eq('id', id).single(),
      supabase
        .from('recordings')
        .select('*')
        .eq('class_id', id)
        .eq('is_visible', true)
        .order('sort_order', { ascending: true })
        .order('recorded_at', { ascending: true })
        .order('created_at', { ascending: true }),
      user?.id
        ? supabase.from('recording_progress').select('recording_id').eq('student_id', user.id)
        : Promise.resolve({ data: [] as { recording_id: string }[], error: null }),
      user?.id
        ? supabase.from('recording_positions').select('recording_id').eq('student_id', user.id)
        : Promise.resolve({ data: [] as { recording_id: string }[], error: null }),
    ]);

    if (!classData) {
      navigate('/dashboard');
      return;
    }

    setCls(classData as PortalClass);
    setRecordings((recData ?? []) as Recording[]);
    setCompletedIds(
      new Set((progressData ?? []).map((p: { recording_id: string }) => p.recording_id))
    );
    setWatchedIds(
      new Set((positionsData ?? []).map((p: { recording_id: string }) => p.recording_id))
    );
    setLoading(false);
  }, [id, navigate, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCompleted = async (recordingId: string) => {
    if (!user) return;
    // Ref-based guard is synchronous — protects against rapid double-clicks
    // that would otherwise bypass the state-based `toggling` check within
    // the same render tick.
    if (togglingRef.current.has(recordingId)) return;
    togglingRef.current.add(recordingId);
    setToggling(recordingId);
    const isDone = completedIds.has(recordingId);

    setCompletedIds(prev => {
      const next = new Set(prev);
      if (isDone) next.delete(recordingId);
      else next.add(recordingId);
      return next;
    });

    const { error } = isDone
      ? await supabase
          .from('recording_progress')
          .delete()
          .eq('recording_id', recordingId)
          .eq('student_id', user.id)
      : await supabase
          .from('recording_progress')
          .insert({ student_id: user.id, recording_id: recordingId });

    if (error) {
      setCompletedIds(prev => {
        const next = new Set(prev);
        if (isDone) next.add(recordingId);
        else next.delete(recordingId);
        return next;
      });
      if (import.meta.env.DEV) console.error('toggleCompleted failed:', error);
    }
    togglingRef.current.delete(recordingId);
    setToggling(null);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-5 w-32 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="h-8 w-64 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="h-96 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
      </div>
    );
  }

  if (!cls) return null;

  const total = recordings.length;
  const completed = recordings.filter(r => completedIds.has(r.id)).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // recordings hold the natural ascending order; numbers follow that order.
  const numbered = recordings.map((rec, idx) => ({ rec, number: idx + 1 }));
  const displayed = ascending ? numbered : [...numbered].reverse();

  return (
    <>
      <div className="space-y-5">
        <button
          onClick={() => navigate(`/dashboard/kurse/${id}`)}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-neutral-400 hover:text-[#970000] dark:hover:text-red-400 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Zurück zum Kurs
        </button>

        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-1">
              {cls.title}
            </p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-neutral-100">Alle Lektionen</h1>
          </div>
          {total > 1 && (
            <button
              onClick={() => { setAscending(v => !v); setVisibleCount(10); }}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-neutral-300 border border-gray-200 dark:border-neutral-800 rounded-full px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
              title="Reihenfolge umkehren"
            >
              <ArrowDownUp className="h-3.5 w-3.5" />
              {ascending ? 'Älteste zuerst' : 'Neueste zuerst'}
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">Fortschritt</span>
            <span className="text-sm text-gray-500 dark:text-neutral-400">
              {completed} / {total} Lektionen ·{' '}
              <span className="font-bold text-gray-900 dark:text-neutral-100">{pct}%</span>
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#970000] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
          {recordings.length === 0 ? (
            <div className="p-10 text-center">
              <BookOpen className="h-10 w-10 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400 dark:text-neutral-500">Noch keine Lektionen hinterlegt.</p>
            </div>
          ) : (
            <>
            <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
              {displayed.slice(0, visibleCount).map(({ rec, number }) => (
                <LessonRow
                  key={rec.id}
                  recording={rec}
                  number={number}
                  done={completedIds.has(rec.id)}
                  isNew={isNewRecording(rec, completedIds.has(rec.id) || watchedIds.has(rec.id))}
                  toggling={toggling === rec.id}
                  onToggleCompleted={() => toggleCompleted(rec.id)}
                  onPlay={() => openLesson(rec)}
                />
              ))}
            </ul>
            {visibleCount < displayed.length && (
              <div className="p-4 border-t border-gray-200 dark:border-neutral-800 text-center">
                <button
                  onClick={() => setVisibleCount(v => v + 10)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#970000] dark:text-red-400 hover:underline"
                >
                  Mehr anzeigen ({displayed.length - visibleCount} verbleibend)
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentCourseLessonsPage;
